import {
  type SccrsWithdrawFormValues,
  useSccrsFormLogic,
} from '@/entities/sccrs';
import { useEffect, useMemo, useState } from 'react';
import { useWithdrawOperationData } from '@/shared/lib';
import {
  type FiatWithdrawAssetOption,
  getFiatWithdrawAssetOptions,
  useWithdrawTotalAmount,
} from '@/entities/operation';
import type { CommissionResponse } from '@/shared/types';
import {
  type CurrencyAssetOption,
  useWithdrawCommission,
  type WithdrawAmountValues,
} from '@/features/withdraw-sccrs';

interface UseDepositSccrsFormLogicOptions {
  initialData?: Partial<SccrsWithdrawFormValues>;
  onSuccess?: (data: SccrsWithdrawFormValues) => void;
}

export const useWithdrawSccrsForm = ({
  initialData,
  onSuccess,
}: UseDepositSccrsFormLogicOptions) => {
  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyAssetOption | undefined
  >();
  const [selectedIncludeFee, setSelectedIncludeFee] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<
    CommissionResponse | undefined
  >(undefined);
  const [totalAmount, setTotalAmount] = useState(0);

  const { ewallet, assetsIconsData, assetsData, mainAccount } =
    useWithdrawOperationData();

  const {
    sccrsForm,
    watchedValues,
    platformOptions,
    debouncedAmount,
    userProfileInfoError,
    gamingHallsError,
    accountNumber,
    isUserProfileInfoLoading,
    depositWithdrawAssetsData,
    isGamingHallsLoading,
  } = useSccrsFormLogic({
    initialData,
    isMaxAmount: true,
    maxAvailable: selectedIncludeFee
      ? selectedCurrency?.available
      : (selectedCurrency?.available ?? 0) -
        (selectedCommission?.feeAmount ?? 0),
    isMinAmount: true,
    currentMinAmount: selectedCurrency?.min,
    currentMaxAmount: selectedCurrency?.max,
  });

  const currencyOptions = useMemo<FiatWithdrawAssetOption[]>(() => {
    return getFiatWithdrawAssetOptions({
      key: 'sccrs',
      assetsData,
      assetsIconsData,
      depositWithdrawAssetsData,
      mainAccount,
      ewallet,
    });
  }, [
    assetsData,
    assetsIconsData,
    ewallet,
    depositWithdrawAssetsData,
    mainAccount,
  ]);

  useEffect(() => {
    setSelectedIncludeFee(watchedValues.includeFee ?? false);
  }, [watchedValues.includeFee]);

  useEffect(() => {
    if (!currencyOptions || currencyOptions.length === 0) return;

    const currencyItem = currencyOptions.find(
      a =>
        a.code === watchedValues.currencyCode ||
        a.code === initialData?.currencyCode
    );

    if (currencyItem) {
      setSelectedCurrency(currencyItem);
      sccrsForm.setValue('currencyCode', currencyItem.code);
    }
  }, [watchedValues.currencyCode, initialData?.currencyCode, currencyOptions]);

  const handleMaxClick = () => {
    if (selectedCurrency) {
      sccrsForm.setValue('amount', String(selectedCurrency.available));
    }
  };

  const amountError = sccrsForm.formState.errors.amount;
  const hasAmountError = !!amountError;

  const commissionEnabled = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const isTyping = watchedValues.amount !== debouncedAmount;
    if (isTyping) {
      return false;
    }

    const amount = Number(debouncedAmount);

    if (selectedCurrency?.min !== undefined && selectedCurrency?.min > amount) {
      return false;
    }

    if (
      selectedCurrency?.available !== undefined &&
      amount > selectedCurrency?.available
    ) {
      return false;
    }

    const hasValidDebouncedAmount = !!debouncedAmount;
    const hasCurrency = !!selectedCurrency?.code;

    return hasValidDebouncedAmount && hasCurrency;
  }, [
    debouncedAmount,
    selectedCurrency?.code,
    hasAmountError,
    watchedValues.amount,
    watchedValues.includeFee,
  ]);

  const { commissionData, commissionError, isCommissionFetching } =
    useWithdrawCommission({
      currencyCode: selectedCurrency?.code || '',
      amount: debouncedAmount,
      onTop: watchedValues.includeFee,
      enabled: commissionEnabled,
    });

  useEffect(() => {
    setSelectedCommission(commissionData);
  }, [commissionData, isCommissionFetching]);

  const amountValues: WithdrawAmountValues = {
    amount: watchedValues.amount,
    includeFee: watchedValues.includeFee ?? false,
  };
  const { totalAmount: calculatedTotalAmount } = useWithdrawTotalAmount<
    SccrsWithdrawFormValues,
    WithdrawAmountValues
  >({
    form: sccrsForm,
    watchedValues: amountValues,
    commissionData,
    assetCode: selectedCurrency?.code,
    isCommissionLoading: isCommissionFetching,
    additionalDependencies: [selectedCurrency?.code],
    validationDependencies: [
      amountValues.amount,
      selectedCurrency?.code,
      totalAmount,
    ],
  });

  useEffect(() => {
    setTotalAmount(calculatedTotalAmount);
  }, [calculatedTotalAmount]);

  const isGettingCommission = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const hasAmount = !!watchedValues.amount;
    const hasCurrency = !!selectedCurrency?.code;
    const isTyping = watchedValues.amount !== debouncedAmount;

    return (isTyping && hasAmount && hasCurrency) || isCommissionFetching;
  }, [
    watchedValues.amount,
    debouncedAmount,
    selectedCurrency?.code,
    hasAmountError,
    isCommissionFetching,
  ]);

  const isSubmitInProgressWithoutErrors = !(
    isGettingCommission &&
    (Boolean(commissionError) ||
      Object.keys(sccrsForm.formState.errors).length > 0)
  );

  const handleFormSubmit = async (data: SccrsWithdrawFormValues) => {
    if (!isSubmitInProgressWithoutErrors) {
      return;
    }

    onSuccess?.(data);
  };

  return {
    sccrsForm,
    watchedValues,
    currencyOptions,
    platformOptions,
    selectedCurrency,
    totalAmount,
    hasWithdrawFee: commissionData ? commissionData.feeAmount > 0 : false,
    isFromDataLoading:
      isUserProfileInfoLoading ||
      isGamingHallsLoading ||
      currencyOptions.length === 0,
    formDataError: userProfileInfoError || gamingHallsError,
    commissionData,
    handleFormSubmit,
    error: commissionError,
    handleMaxClick,
    isLoading: isGettingCommission,
    accountNumber,
    isDirty: sccrsForm.formState.isDirty,
  };
};
